import { useCallback, useState } from "react";
import useCreatePathPickRequests from "@/features/pathpick/hooks/mutation/useCreatePathPickRequests";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  PathPickMutationProduct,
  PathPickProductRequestKind,
} from "@/features/pathpick/types";

// 재생성 대상 — 상품 하나와 그 상품에서 다시 신청할 채널(request_types) 묶음.
export interface PathPickRetryTarget {
  product: PathPickMutationProduct;
  kinds: PathPickProductRequestKind[];
}

interface Options {
  onSuccess?: () => void;
}

// 실패한 상품 등록 요청 재생성 — 상품별 처리 내역의 단건/일괄 호출부가 공유한다.
// 요청 바디가 requestTypes × products 교차 구조라, 실패 채널 조합이 같은 상품끼리 묶어
// 그룹당 한 번만 보낸다 (조합을 합쳐 보내면 이미 성공한 채널까지 다시 신청된다).
const usePathPickRetryRequests = () => {
  const { t } = useI18nHelper();
  const { info } = useAppToasts({ id: "GLOBAL" });
  const { mutateAsync: createRequests } = useCreatePathPickRequests();
  // 그룹을 병렬로 보내므로 mutation의 isPending 대신 호출 단위 진행 상태를 따로 둔다.
  const [isPending, setIsPending] = useState<boolean>(false);

  const retryRequests = useCallback(
    async (targets: PathPickRetryTarget[], options?: Options) => {
      const groups = new Map<
        string,
        {
          requestTypes: PathPickProductRequestKind[];
          products: PathPickMutationProduct[];
        }
      >();

      targets.forEach(({ product, kinds }) => {
        if (kinds.length === 0) {
          return;
        }
        const requestTypes = Array.from(new Set(kinds)).sort();
        const key = requestTypes.join("|");
        const group = groups.get(key);
        if (group) {
          group.products.push(product);
          return;
        }
        groups.set(key, { requestTypes, products: [product] });
      });

      if (groups.size === 0) {
        return;
      }

      setIsPending(true);
      // 개별 실패 토스트는 useCreatePathPickRequests.onError가 이미 띄우므로 여기서는 결과만 센다.
      const results = await Promise.allSettled(
        Array.from(groups.values(), (body) => createRequests(body))
      );
      setIsPending(false);

      const requestedCount = Array.from(groups.values()).reduce(
        (count, body, index) =>
          results[index].status === "fulfilled"
            ? count + body.products.length
            : count,
        0
      );

      // 전부 실패했으면 선택 해제·모달 닫기도 하지 않는다 — 사용자가 그대로 다시 시도할 수 있게 둔다.
      if (requestedCount === 0) {
        return;
      }

      info(
        t("path_pick:sync_download_history.retry_result.success", {
          count: requestedCount,
        })
      );
      options?.onSuccess?.();
    },
    [createRequests, info, t]
  );

  return { retryRequests, isPending };
};

export default usePathPickRetryRequests;
